import RHFDynamicFormFields from "$routes/_lib/rhf-dynamic-form-fields/rhf-dynamic-form-fields";
import { useFieldSchema } from "$routes/_lib/validation/use-field-schema";
import ValidationContext from "$routes/_lib/validation/validation-context";
import type { CatalogueCoding } from "elephant-web/gql";
import { path } from "ramda";
import { type ComponentProps, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import * as Yup from "yup";
import type { InvestigationCard } from "./investigation-card";

export const InvestigationCardField: FC<
  Omit<
    ComponentProps<typeof InvestigationCard>,
    "outcome" | "request" | "isDirty" | "isValid" | "investigationId"
  > & {
    index: number;
    fieldNames: { investigations: string };
    onInvestigationChanged?: (formItem: any) => void;
    testValidationProps?: {
      onPhotoConfirmed: (imageSrc: string) => void;
      testRequiresValidation: ({
        coding,
      }: {
        coding?: CatalogueCoding;
      }) => boolean;
    };
  }
> = ({
  catalogueItem,
  index,
  fieldNames,
  onInvestigationChanged,
  testValidationProps,
}) => {
  const {
    formState: { touchedFields },
    setValue,
    register,
    watch,
  } = useFormContext();

  const name = `${fieldNames.investigations}.${index}`;

  const isTouched = !!path(name.split("."), touchedFields);

  // Register virtual fields
  useEffect(() => {
    register(`${name}.investigation`);
    register(`${name}.isRequested`);
  }, [register, name]);

  const lazy = useFieldSchema(name);
  const outcomeValidationSchema = Yup.reach(
    lazy,
    "outcome",
    catalogueItem,
    undefined,
  ) as Yup.AnyObjectSchema;
  const parent = `${name}.outcome`;

  // As soon as the form is touched, set `isRequested` to false
  useEffect(() => {
    if (isTouched) {
      setValue(`${name}.isRequested`, false);
    }
  }, [setValue, isTouched, name]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: it's complaining that data[fieldNames.investigations][index] is not defined
  useEffect(() => {
    if (!onInvestigationChanged) return;

    const { unsubscribe } = watch((data, { name }) => {
      if (
        (!!name && !name.startsWith(fieldNames.investigations)) ||
        !data[fieldNames.investigations][index]
      ) {
        return;
      }

      onInvestigationChanged(data[fieldNames.investigations][index]);
    });

    return () => unsubscribe();
  }, []);

  // Uncomment if we turn test result validation back on
  // // Track previous result value so we only show webcam dialog if it changes
  // const prevResultRef = useRef(catalogueItem?.result);
  // useEffect(() => {
  //   if (
  //     !testValidationProps ||
  //     !testValidationProps.testRequiresValidation(catalogueItem)
  //   ) {
  //     return;
  //   }

  //   const { unsubscribe } = watch((data) => {
  //     const currResult =
  //       data[fieldNames.investigations][index]?.outcome?.result;
  //     if (currResult && currResult !== prevResultRef.current) {
  //       prevResultRef.current = currResult;
  //       setIsWebcamDialogOpen(true);
  //     }
  //   });
  //   return () => unsubscribe();
  // }, [testValidationProps, catalogueItem]);

  return (
    <>
      <ValidationContext.Provider
        value={{
          validationSchema: outcomeValidationSchema,
          resolvedAt: parent,
        }}
      >
        <RHFDynamicFormFields
          schema={catalogueItem.result.resultSchema}
          parent={parent}
        />
      </ValidationContext.Provider>
    </>
  );
};
